/**
 * 瓦片缓存存储 —— IndexedDB（持久）与内存（兜底）走**同一套逻辑**。
 *
 * ## 设计：逻辑与"存哪儿"分开
 *
 * 缓存的行为里只有很小一部分是 IndexedDB 特有的（打开库、读一条、写一条、删一批），
 * 其余全是纯粹的策略：TTL、LRU 淘汰、容量上限、覆盖索引维护、命中时间戳的写放大抑制。
 * 如果把这些策略各写两遍（IDB 版 + 内存版），迟早会出现"内存版能省下 100 MB、
 * IDB 版一路涨到配额爆掉"这种只在现场发作的差异。所以：
 *
 * - {@link TilePersistence} —— 只负责读写的 6 个原语，两种实现；
 * - {@link createTileStore} —— 全部策略，两种实现共用。
 *
 * ## 元数据单独一张表
 *
 * 覆盖判定要列举"这个源有哪些瓦片"，淘汰要按最后使用时间排序 —— 这两件事都不需要
 * 瓦片字节。把它们放在同一张表里意味着每次列清单都要把几百 MB 的 `ArrayBuffer`
 * 读进内存（`getAll()` 会把数据一起反序列化），这在野外低端笔记本上是能明显感觉到的卡顿。
 * 因此 `tiles`（字节）与 `meta`（键 + 大小 + 时间戳）分开，清单只读 `meta`。
 *
 * ## 容量与失效规则（写死在常量里，界面会照实显示）
 *
 * - **每个源**最多 {@link TILE_CACHE_LIMITS}.maxTiles 块 / maxBytes 字节，超出后按
 *   "最久未使用"淘汰。按源分开而不是全局共享：换过底图的用户不该因为旧的卫星影像
 *   把刚缓存好的地形图挤掉。
 * - 正常瓦片 TTL 30 天；**离线模式下过期的瓦片照旧使用**（野外唯一可用的地图不能因为
 *   "过期"就不显示），在线模式下过期瓦片会触发一次后台刷新（`tileProtocol` 负责）。
 * - "这块瓦片上游没有数据"（404）也记一条，TTL 24 小时：不记的话每次平移都会把
 *   同一批 404 再打一遍，而记录太久又会在上游补图后一直显示空白。
 */

import type { TileCoord } from './tiles';
import { parseTileKey, tileKey } from './tiles';

/** `ok` = 真实瓦片；`empty` = 上游明确回复"没有这块"（404/204）。 */
export type TileEntryState = 'ok' | 'empty';

export interface TileEntryMeta {
  readonly key: string;
  readonly sourceId: string;
  readonly tile: TileCoord;
  readonly bytes: number;
  readonly contentType: string;
  readonly state: TileEntryState;
  readonly storedAt: number;
  readonly lastUsedAt: number;
  /** 失效时刻；`null` 表示不失效（目前只有"离线专用的重要区域"会用，暂未使用） */
  readonly expiresAt: number | null;
}

export interface TileEntry extends TileEntryMeta {
  readonly data: ArrayBuffer;
}

export interface TileCacheLimits {
  readonly maxTiles: number;
  readonly maxBytes: number;
}

export const TILE_CACHE_LIMITS: TileCacheLimits = Object.freeze({
  maxTiles: 4000,
  maxBytes: 192 * 1024 * 1024,
});

/** 正常瓦片的 TTL（30 天）。 */
export const TILE_TTL_MS = 30 * 24 * 60 * 60 * 1000;

/** "上游没有这块瓦片"的记录 TTL（24 小时）。 */
export const EMPTY_TILE_TTL_MS = 24 * 60 * 60 * 1000;

/** 命中时更新 `lastUsedAt` 的最小间隔。每次命中都写一次库会让读变成写，得不偿失。 */
export const TOUCH_INTERVAL_MS = 10 * 60 * 1000;

/** 每写多少次做一次淘汰检查。逐次检查会让每块瓦片都多一次全表排序。 */
export const EVICT_EVERY_PUTS = 32;

export interface TileCacheStats {
  readonly tiles: number;
  readonly bytes: number;
  readonly bySource: Readonly<Record<string, { readonly tiles: number; readonly bytes: number }>>;
}

/** 某个源的键清单：覆盖判定只需要键。 */
export type TileKeyIndex = ReadonlySet<string>;

export interface TileStore {
  /** 读一块瓦片（会按需更新 `lastUsedAt`）。**不**判断 TTL —— 那是调用方的策略。 */
  get(key: string): Promise<TileEntry | null>;
  put(entry: TileEntry): Promise<void>;
  /** 某个源已缓存的所有缓存键。用于覆盖判定与预缓存去重。 */
  keys(sourceId: string): Promise<TileKeyIndex>;
  remove(keys: readonly string[]): Promise<void>;
  /** 清空（可只清某个源）。 */
  clear(sourceId?: string): Promise<void>;
  stats(): Promise<TileCacheStats>;
  /** 主动淘汰 + 清理过期记录，返回删除的条数。 */
  prune(): Promise<number>;
}

/**
 * 存储原语。刻意保持极小：任何策略都不允许下沉到这里，
 * 否则两套实现又会开始漂移。
 */
export interface TilePersistence {
  read(key: string): Promise<TileEntry | null>;
  loadMeta(sourceId?: string): Promise<readonly TileEntryMeta[]>;
  write(entry: TileEntry): Promise<void>;
  touch(key: string, lastUsedAt: number): Promise<void>;
  delete(keys: readonly string[]): Promise<void>;
  clear(sourceId?: string): Promise<void>;
}

/* ───────────────────────── 纯策略 ───────────────────────── */

/**
 * LRU 淘汰计划：返回应当删除的键。
 *
 * 先按"最久未使用"排序再删，直到同时满足条数与字节两个上限。
 * 两个上限都必须满足 —— 只看条数的话，512×512 的影像瓦片（每块 50–200 KB）
 * 会在条数达标时把几百 MB 塞满磁盘配额，之后**所有**写入都开始失败。
 */
export function planEviction(
  metas: readonly TileEntryMeta[],
  limits: TileCacheLimits,
): readonly string[] {
  let tiles = metas.length;
  let bytes = metas.reduce((sum, meta) => sum + meta.bytes, 0);
  if (tiles <= limits.maxTiles && bytes <= limits.maxBytes) return [];

  const byAge = [...metas].sort((a, b) => a.lastUsedAt - b.lastUsedAt);
  const evicted: string[] = [];
  for (const meta of byAge) {
    if (tiles <= limits.maxTiles && bytes <= limits.maxBytes) break;
    evicted.push(meta.key);
    tiles -= 1;
    bytes -= meta.bytes;
  }
  return evicted;
}

/** 已过期的记录（`empty` 记录到期即清；`ok` 记录过期只是"该刷新"，不是"该删"）。 */
export function expiredKeys(metas: readonly TileEntryMeta[], now: number): readonly string[] {
  return metas
    .filter((meta) => meta.state === 'empty' && meta.expiresAt !== null && meta.expiresAt <= now)
    .map((meta) => meta.key);
}

/** 构造一条缓存记录，并顺手算出 TTL。 */
export function entryFor(input: {
  readonly sourceId: string;
  readonly tile: TileCoord;
  readonly data: ArrayBuffer;
  readonly contentType: string;
  readonly state: TileEntryState;
  readonly now: number;
}): TileEntry {
  const ttl = input.state === 'empty' ? EMPTY_TILE_TTL_MS : TILE_TTL_MS;
  return {
    key: tileKey(input.sourceId, input.tile),
    sourceId: input.sourceId,
    tile: input.tile,
    bytes: input.data.byteLength,
    contentType: input.contentType,
    state: input.state,
    storedAt: input.now,
    lastUsedAt: input.now,
    expiresAt: input.now + ttl,
    data: input.data,
  };
}

/** 这条记录还能用吗（离线判定：只看有没有数据，不看 TTL）。 */
export function isUsable(entry: TileEntryMeta): boolean {
  return entry.state === 'ok';
}

/** 这条记录是不是过期了（在线模式下过期会触发后台刷新）。 */
export function isExpired(entry: TileEntryMeta, now: number): boolean {
  return entry.expiresAt !== null && entry.expiresAt <= now;
}

/* ───────────────────────── 内存实现 ───────────────────────── */

export function createMemoryPersistence(): TilePersistence {
  const entries = new Map<string, TileEntry>();
  return {
    async read(key) {
      return entries.get(key) ?? null;
    },
    async loadMeta(sourceId) {
      return [...entries.values()]
        .filter((entry) => sourceId === undefined || entry.sourceId === sourceId)
        .map(stripData);
    },
    async write(entry) {
      entries.set(entry.key, entry);
    },
    async touch(key, lastUsedAt) {
      const entry = entries.get(key);
      if (entry !== undefined) entries.set(key, { ...entry, lastUsedAt });
    },
    async delete(keys) {
      for (const key of keys) entries.delete(key);
    },
    async clear(sourceId) {
      if (sourceId === undefined) {
        entries.clear();
        return;
      }
      for (const [key, entry] of [...entries]) {
        if (entry.sourceId === sourceId) entries.delete(key);
      }
    },
  };
}

function stripData(entry: TileEntry): TileEntryMeta {
  return {
    key: entry.key,
    sourceId: entry.sourceId,
    tile: entry.tile,
    bytes: entry.bytes,
    contentType: entry.contentType,
    state: entry.state,
    storedAt: entry.storedAt,
    lastUsedAt: entry.lastUsedAt,
    expiresAt: entry.expiresAt,
  };
}

export function toMeta(entry: TileEntry): TileEntryMeta {
  return stripData(entry);
}

/* ───────────────────────── 策略核心 ───────────────────────── */

export interface TileStoreOptions {
  readonly persistence: TilePersistence;
  readonly limits?: TileCacheLimits;
  readonly now?: () => number;
}

export function createTileStore(options: TileStoreOptions): TileStore {
  const persistence = options.persistence;
  const limits = options.limits ?? TILE_CACHE_LIMITS;
  const now = options.now ?? ((): number => Date.now());

  /** 每个源的元数据索引。第一次用到某个源时从持久层加载，之后就地维护。 */
  const index = new Map<string, Map<string, TileEntryMeta>>();
  let putsSinceEvict = 0;

  async function indexFor(sourceId: string): Promise<Map<string, TileEntryMeta>> {
    const existing = index.get(sourceId);
    if (existing !== undefined) return existing;
    const loaded = new Map<string, TileEntryMeta>();
    for (const meta of await persistence.loadMeta(sourceId)) loaded.set(meta.key, meta);
    index.set(sourceId, loaded);
    return loaded;
  }

  async function prune(): Promise<number> {
    const all = await persistence.loadMeta();
    const at = now();
    const doomed = new Set<string>(expiredKeys(all, at));
    const survivors = all.filter((meta) => !doomed.has(meta.key));
    for (const key of planEviction(survivors, limits)) doomed.add(key);
    if (doomed.size === 0) return 0;

    await persistence.delete([...doomed]);
    // 索引就地更新，避免下次 keys() 又全量加载
    for (const sourceIndex of index.values()) {
      for (const key of doomed) sourceIndex.delete(key);
    }
    return doomed.size;
  }

  return {
    async get(key): Promise<TileEntry | null> {
      const entry = await persistence.read(key);
      if (entry === null) return null;
      const at = now();
      if (at - entry.lastUsedAt < TOUCH_INTERVAL_MS) return entry;
      // 只在"很久没碰过"时才写回：命中转成写会让读放大好几个数量级
      await persistence.touch(key, at);
      index.get(entry.sourceId)?.set(key, toMeta({ ...entry, lastUsedAt: at }));
      return { ...entry, lastUsedAt: at };
    },

    async put(entry): Promise<void> {
      await persistence.write(entry);
      const sourceIndex = await indexFor(entry.sourceId);
      sourceIndex.set(entry.key, toMeta(entry));
      putsSinceEvict += 1;
      if (putsSinceEvict >= EVICT_EVERY_PUTS) {
        putsSinceEvict = 0;
        await prune();
      }
    },

    async keys(sourceId): Promise<TileKeyIndex> {
      return new Set((await indexFor(sourceId)).keys());
    },

    async remove(keys): Promise<void> {
      await persistence.delete(keys);
      for (const key of keys) {
        const parsed = parseTileKey(key);
        if (parsed !== null) index.get(parsed.sourceId)?.delete(key);
      }
    },

    async clear(sourceId): Promise<void> {
      await persistence.clear(sourceId);
      if (sourceId === undefined) index.clear();
      else index.delete(sourceId);
    },

    async stats(): Promise<TileCacheStats> {
      const all = await persistence.loadMeta();
      const bySource: Record<string, { tiles: number; bytes: number }> = {};
      let tiles = 0;
      let bytes = 0;
      for (const meta of all) {
        tiles += 1;
        bytes += meta.bytes;
        const bucket = bySource[meta.sourceId] ?? { tiles: 0, bytes: 0 };
        bucket.tiles += 1;
        bucket.bytes += meta.bytes;
        bySource[meta.sourceId] = bucket;
      }
      return { tiles, bytes, bySource };
    },

    prune,
  };
}
